'use client';

/**
 * PulseIndicator Component
 *
 * A small pulsing dot indicator for live/syncing/active states.
 * Supports palette colors (success, warning, error, info) and NeonAccent
 * colors from the design system.
 *
 * Usage:
 * ```tsx
 * <PulseIndicator />
 * <PulseIndicator color="error" label="Disconnected" />
 * <PulseIndicator color="pink" size="medium" label="Live" />
 * ```
 */

import Box from '@mui/material/Box';
import type { SxProps, Theme } from '@mui/material/styles';
import { keyframes, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { NeonAccent } from '../../theme/palette';
import { getNeonStyles } from '../../theme/palette';

// ============================================================================
// Types
// ============================================================================

type PaletteColor = 'success' | 'warning' | 'error' | 'info';

export interface PulseIndicatorProps {
  /**
   * Dot color. Palette keys resolve via theme.palette, otherwise treated as
   * a NeonAccent key from the design-system palette.
   * @default 'success'
   */
  color?: PaletteColor | NeonAccent;

  /**
   * Dot size.
   * - small = 8px
   * - medium = 12px
   * @default 'small'
   */
  size?: 'small' | 'medium';

  /** Optional text label displayed next to the dot. */
  label?: string;

  /** Additional styles applied to the root container. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Animation
// ============================================================================

const pulseRing = keyframes`
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(2.2);
    opacity: 0;
  }
`;

// ============================================================================
// Helpers
// ============================================================================

const PALETTE_COLORS = new Set<string>(['success', 'warning', 'error', 'info']);

const SIZE_MAP: Record<'small' | 'medium', number> = {
  small: 8,
  medium: 12,
};

function resolveColor(color: PaletteColor | NeonAccent, theme: Theme): string {
  if (PALETTE_COLORS.has(color)) {
    return theme.palette[color as PaletteColor].main;
  }

  const isDark = theme.palette.mode === 'dark';
  return getNeonStyles(color as NeonAccent, isDark).base;
}

// ============================================================================
// Component
// ============================================================================

function PulseIndicator({ color = 'success', size = 'small', label, sx }: PulseIndicatorProps) {
  const theme = useTheme();
  const resolvedColor = resolveColor(color, theme);
  const dotSize = SIZE_MAP[size];

  return (
    <Box
      data-testid="pulse-indicator"
      sx={[
        {
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.75,
        },
        ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
      ]}
    >
      <Box
        sx={{
          position: 'relative',
          width: dotSize,
          height: dotSize,
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            borderRadius: '50%',
            backgroundColor: resolvedColor,
            animation: `${pulseRing} 2s ease-out infinite`,
            '@media (prefers-reduced-motion: reduce)': {
              animation: 'none',
            },
          }}
        />

        <Box
          sx={{
            position: 'relative',
            width: dotSize,
            height: dotSize,
            borderRadius: '50%',
            backgroundColor: resolvedColor,
            boxShadow: `0 0 6px 1px ${resolvedColor}`,
          }}
        />
      </Box>

      {label && (
        <Typography variant="caption" sx={{ color: 'text.secondary', lineHeight: 1 }}>
          {label}
        </Typography>
      )}
    </Box>
  );
}

PulseIndicator.displayName = 'PulseIndicator';

export { PulseIndicator };
export default PulseIndicator;
